import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { formatINR } from '../../utils/currency';
import { getFullImageUrl } from '../../utils/imageUtils';
import { getProductSellingPrice } from '../../utils/pricing';

type Product = {
  _id: string;
  name: string;
  slug: string;
  price?: { mrp?: number; selling?: number };
  images?: string[] | { url: string }[];
};

type Props = {
  title: string;
  layout: 'slider' | 'grid';
  products: Product[];
};

const getImg = (p: Product) => {
  const img = p.images?.[0];
  if (!img) return 'https://via.placeholder.com/300?text=Product';
  return getFullImageUrl(typeof img === 'string' ? img : img.url);
};

const SLIDER_CARD =
  'w-[calc(50vw-1.25rem)] min-w-[148px] max-w-[200px] sm:w-[200px] sm:min-w-[200px] md:w-[220px] md:min-w-[220px]';

const ProductCard: React.FC<{ product: Product }> = ({ product }) => {
  const selling = getProductSellingPrice(product);
  const mrp = product.price?.mrp || 0;
  const slug = product.slug || product._id;

  return (
    <Link
      to={`/product/${slug}`}
      className="group flex h-full w-full min-w-0 flex-col bg-white rounded-2xl border border-gray-100 overflow-hidden hover:shadow-lg transition-shadow"
    >
      <div className="aspect-square w-full bg-gray-50 overflow-hidden shrink-0">
        <img
          src={getImg(product)}
          alt={product.name}
          loading="lazy"
          className="w-full h-full object-contain p-3 group-hover:scale-105 transition-transform duration-300"
        />
      </div>
      <div className="flex flex-1 flex-col p-3 min-h-[4.5rem]">
        <p className="text-sm font-medium text-gray-900 line-clamp-2 leading-snug">{product.name}</p>
        <div className="mt-auto pt-2">
          <p className="text-base font-bold text-slate-900">{formatINR(selling)}</p>
          {mrp > selling && (
            <p className="text-xs text-gray-400 line-through">{formatINR(mrp)}</p>
          )}
        </div>
      </div>
    </Link>
  );
};

const BrandProductSection: React.FC<Props> = ({ title, layout, products }) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  if (!products.length) return null;

  const scroll = (dir: number) => {
    const el = scrollRef.current;
    if (!el) return;
    const step = el.clientWidth * 0.85;
    el.scrollBy({ left: dir * step, behavior: 'smooth' });
  };

  return (
    <section className="border-t border-gray-100 bg-white py-8 md:py-10">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-5 flex items-center justify-between gap-4">
          <div className="flex min-w-0 items-center gap-3">
            <span className="h-9 w-1 shrink-0 rounded-full bg-gradient-to-b from-blue-500 to-cyan-500" />
            <h2 className="truncate text-lg font-bold text-gray-900 md:text-xl">{title}</h2>
            <span className="text-xs text-gray-400 shrink-0">{products.length} items</span>
          </div>
          {layout === 'slider' && products.length > 2 && (
            <div className="flex gap-2 shrink-0">
              <button
                type="button"
                onClick={() => scroll(-1)}
                className="p-2 rounded-full border border-gray-200 hover:bg-gray-50"
                aria-label="Scroll left"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => scroll(1)}
                className="p-2 rounded-full border border-gray-200 hover:bg-gray-50"
                aria-label="Scroll right"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>

        {layout === 'grid' ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 md:gap-4">
            {products.map((p) => (
              <div key={p._id} className="min-w-0 h-full">
                <ProductCard product={p} />
              </div>
            ))}
          </div>
        ) : (
          <div
            ref={scrollRef}
            className="flex items-stretch gap-3 md:gap-4 overflow-x-auto pb-2 snap-x snap-mandatory scroll-smooth [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            {products.map((p) => (
              <div key={p._id} className={`snap-start shrink-0 h-auto ${SLIDER_CARD}`}>
                <ProductCard product={p} />
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default BrandProductSection;
